import { WorldPoint, Direction, all_directions, opposite, Route } from './grid';
import { EnemyFinder, Building, BuildingType, building_description, cost_for_building } from './building';
import { Road } from './road';
import { Distributor } from './graph';
import { Command, CreateBuildingCommand, CommandViability, PacketBuffer } from './command';
import { AtlasCoord, JsonDictionaryWrapper } from './utils';
import { Resource, Purse } from './resources';
import { PQueue, PQueueRecord } from './pqueue';
import { Mineable, Deposit } from './deposit';
import { Enemy, BuildingFinder, RouteFinder } from './enemy';
import { Shot, FireControl, shot_from_json } from './gun';

type Ornament = {
    sprite_root: AtlasCoord,
    world_root: WorldPoint,
    width: number,
    height: number,
}

class WorldMap {
    public ornaments: Ornament[] = []
    public deposits: Deposit[] = []
    public hq: WorldPoint = new WorldPoint(0, 0)
    public enemy_spawn: WorldPoint = new WorldPoint(0, 60)

    private add_ornament1(i: number, j: number): void {
        this.ornaments.push({
            sprite_root: { i: 6, j: 0 },
            world_root: new WorldPoint(i, j),
            width: 4,
            height: 2,
        })
    }

    private add_rock_deposit(i: number, j: number): void {
        this.deposits.push(new Deposit(Mineable.Rock, new WorldPoint(i, j)))
    }

    private add_uranium_deposit(i: number, j: number): void {
        this.deposits.push(new Deposit(Mineable.Uranium, new WorldPoint(i, j)))
    }

    private add_steel_deposit(i: number, j: number): void {
        this.deposits.push(new Deposit(Mineable.Steel, new WorldPoint(i, j)))
    }

    // might be positive or negative
    public tile_key(wp: WorldPoint): number {
        // hopefully noone makes a map 10k across
        return wp.x + wp.y * 10000
    }

    constructor() {
        this.add_ornament1(5, 3)
        this.add_uranium_deposit(-18, 3)
        this.add_rock_deposit(-3, -10)
        this.add_steel_deposit(-9, -30)
        this.add_rock_deposit(-2, 20)
    }
}

class EnemyDirector {
    static ticks_between_waves: number = 3600
    static ticks_until_first_wave: number = 3600 * 3

    private _ticks_until_wave: number
    private _wave_index: number

    constructor() {
        this._ticks_until_wave = EnemyDirector.ticks_until_first_wave
        this._wave_index = 1
    }

    public tick(): boolean {
        if (this._ticks_until_wave == 0) {
            this._ticks_until_wave = EnemyDirector.ticks_between_waves
            this._wave_index += 1
            return true
        } else {
            this._ticks_until_wave -= 1
            return false
        }
    }

    public send_now(): void {
        this._ticks_until_wave = 0
    }

    public get wave_index(): number {
        return this._wave_index
    }

    public get ticks_until_wave(): number {
        return this._ticks_until_wave
    }

    private toJSON(): any {
        return {
            ticks_until_wave: this.ticks_until_wave,
            wave_index: this.ticks_until_wave,
        }
    }

    public static create_json(obj: JsonDictionaryWrapper): EnemyDirector {
        let ed = new EnemyDirector()
        ed._ticks_until_wave = obj.get_number("ticks_until_wave")
        ed._wave_index = obj.get_number("wave_index")
        return ed
    }
}

export class World implements RouteFinder, EnemyFinder, BuildingFinder, FireControl {
    // by convention [0] is the hq
    public buildings: Building[] = []
    public map: WorldMap
    public distributor: Distributor = new Distributor()
    public roads: Road[] = []
    public enemies: Enemy[] = []
    public shots: Shot[] = []
    public director: EnemyDirector = new EnemyDirector()

    private _rolling_building_id:number = 0

    // this is a cache of whether a cell is occupied right now for purposes of road building
    public occupancy: Set<number> = new Set<number>()

    constructor() {
        this.map = new WorldMap()

        let hq = new Building(this.get_building_id(), BuildingType.Hq, this.map.hq);
        this.place_building(hq);
        hq.magic_build()
        hq.purse.add_resource(5, Resource.Stone)
        hq.purse.add_resource(5, Resource.Steel)
        hq.purse.add_resource(5, Resource.FuelRod)
        hq.purse.add_resource(5, Resource.Uranium)
        hq.purse.add_resource(10, Resource.PavingSlab)
    }

    public get_building_id(): number {
        this._rolling_building_id += 1
        return this._rolling_building_id
    }

    public get money(): Purse {
        return Purse.global
    }

    public can_afford(cost: Purse): boolean {
        return this.money.greater_or_equal(cost)
    }

    public find_mineable(pt: WorldPoint): Mineable | null {
        for (let dep of this.map.deposits) {
            if (dep.position.equal_to(pt)) {
                return dep.mineable
            }
        }

        return null
    }
    
    public mineable_for_footprint(pts: WorldPoint[]): Mineable | null {
        for (let pt of pts) {
            let min = this.find_mineable(pt)
            if (min != null) {
                return min
            }
        }
        return null
    }

    public can_build_road(f: WorldPoint, t: WorldPoint): boolean {
        if (f.equal_to(t)) {
            return false
        }

        return this.find_building(t) != null
    }

    public get_hq(): Building {
        return this.buildings[0]
    }

    public find_enemy(wp: WorldPoint): Enemy | null {
        for (let e of this.enemies) {
            for (let p of e.positions) {
                if (p.equal_to(wp)) {
                    return e
                }
            }
        }

        return null
    }

    public find_building(wp: WorldPoint): Building | null {
        for (let i = 0; i < this.buildings.length; i += 1) {
            let bi = this.buildings[i]
            for (let bwp of this.buildings[i].occupied_tiles()) {
                if (wp.equal_to(bwp)) {
                    return bi
                }
            }
        }

        return null
    }

    public find_enemy_near(wp: WorldPoint, r: number): Enemy | null {
        const r2: number = r * r

        for (let enemy of this.enemies) {
            for (let pos of enemy.positions) {
                let d2 = pos.distance_squared(wp)
                if (d2 < r2) {
                    return enemy
                }
            }
        }

        return null
    }

    public find_live_building_near(wp: WorldPoint, r: number): Building | null {
        const r2: number = r * r

        for (let i = 0; i < this.buildings.length; i += 1) {
            let b = this.buildings[i]
            if (b.inert) {
                continue
            }

            for (let bwp of b.occupied_tiles()) {
                let d2 = bwp.distance_squared(wp)
                if (d2 < r2) {
                    return b
                }
            }
        }

        return null
    }

    // This ignores affordability concerns
    // It determines whether or not you could place a building in that spot
    public can_add_building(bt: BuildingType, wp: WorldPoint): boolean {
        // currently it is easiest to have a temporary building for the calculation
        let b = new Building(this.get_building_id(), bt, wp)

        for (let iwp of b.occupied_tiles()) {
            if (this.find_building(iwp) != null) {
                return false
            }
        }

        return true
    }

    private mark_occupied(wp: WorldPoint): void  {
        this.occupancy.add(this.map.tile_key(wp))
    }

    private mark_unoccupied(wp: WorldPoint): void  {
        this.occupancy.delete(this.map.tile_key(wp))
    }

    public is_occupied(wp: WorldPoint): boolean  {
        return this.occupancy.has(this.map.tile_key(wp))
    }

    private add_funds(delta: Purse): void {
        this.money.add(delta)
    }

    private deduct_funds(delta: Purse): void {
        this.money.subtract(delta)
    }

    private place_building(b: Building) {
        this.buildings.push(b)
        for (let iwp of b.occupied_tiles()) {
            this.mark_occupied(iwp)
        }
        b.did_place()
    }

    public add_enemy(pt: WorldPoint): void {
        this.enemies.push(new Enemy(pt))
    }

    public create_building(cmd: CreateBuildingCommand): Building {
        let bld = new Building(this.get_building_id(), cmd.building_type, cmd.position)
        bld.underlying_resource = this.mineable_for_footprint(bld.occupied_tiles())
        return bld
    }

    public execute_command(cmd: Command): void {
        if (cmd.viability != CommandViability.Ok) {
            return
        }

        this.deduct_funds(cmd.cost)

        switch (cmd.cmd.kind) {
            case 'SendWave': {
                this.director.send_now()
                break
            }

            case 'CreateBuilding': {
                this.place_building(this.create_building(cmd.cmd))
                break
            }

            case 'DeleteBuilding': {
                const b = this.find_building(cmd.cmd.building)
                if (b == null) {
                    throw new Error("No building found at command coords")
                }
                this.delete_building(b)
                break
            }

            case 'DeleteRoad': {
                const start = this.find_building(cmd.cmd.start)
                const finish = this.find_building(cmd.cmd.finish)
                if (start == null || finish == null) {
                    throw new Error("No road found at command coords")
                }

                const r = this.find_road(start, finish)
                if (r == null) {
                    throw new Error("No road found for command")
                }

                this.delete_road(r)
                break
            }

            case 'CreateRoad': {
                for (let wp of cmd.cmd.road.positions()) {
                    this.mark_occupied(wp)
                }
                this.roads.push(cmd.cmd.road)
                break
            }
        }
        
        this.update_routefinder()
    }

    private toJSON(): any {
        return {
            buildings: this.buildings,
            roads: this.roads,
            enemies: this.enemies,
            shots: this.shots,
            rolling_building_id: this._rolling_building_id,
            enemy_director: this.director,
        }
    }

    public load_json(js: JsonDictionaryWrapper) {
        this._rolling_building_id = js.get_number("rolling_building_id")
        this.buildings = js.get_object_array("buildings").all.map(bo => {
            return Building.create_json(bo);
        })
        this.shots = js.get_object_array("shots").all.map(s => shot_from_json(s))
        this.enemies = js.get_object_array("enemies").all.map(e => Enemy.create_json(e))
        this.roads = js.get_object_array("roads").all.map(r => Road.create_json(r, this))
        this.director = EnemyDirector.create_json(js.get_object("enemy_director"))
    }

    private update_routefinder(): void {
        this.distributor.update_from(this.buildings, this.roads)
        for (let e of this.enemies) {
            e.requires_reroute()
        }
    }

    public valid_route_point(wp: WorldPoint): boolean {
        if (this.is_occupied(wp)) {
            return false
        }

        return true
    }

    private shot_hit(hp: number, wp: WorldPoint): void {
        let b = this.find_building(wp)
        if (b != null) {
            b.hp -= hp
            if (b.hp < 0) {
                b.destroy()
            }
        }

        let e = this.find_enemy(wp)
        if (e != null) {
            e.hp -= hp
        }
    }

    private run_wave(): void {
        this.add_enemy(this.map.enemy_spawn.copy())
    }

    public tick(): boolean {
        let tick_ret = false

        if (this.director.tick()) {
            tick_ret = true
            this.run_wave()
        }

        for (let s of this.shots) {
            s.t += 1
            if (s.t == s.transit_ticks) {
                this.shot_hit(s.damage, s.destination)
            }
        }
        this.shots = this.shots.filter(s => s.t < s.transit_ticks)

        this.enemies = this.enemies.filter(e => e.hp >= 0)

        for (let b of this.buildings) {
            b.tick(this, this)
        }

        for (let r of this.roads) {
            r.tick(this, this.distributor)
        }

        for (let e of this.enemies) {
            e.tick(this, this, this)
        }

        return tick_ret
    }

    public fire_shot(s: Shot): void {
        this.shots.push(s)
    }

    private delete_road(r: Road): void {
        for (let wp of r.positions()) {
            this.mark_unoccupied(wp)
        }
        r.will_delete()
        this.roads = this.roads.filter(ar => r != r)
        this.distributor.update_from(this.buildings, this.roads)
    }

    private delete_building(b: Building): void {
        b.purse.clear()
        b.deleted = true

        for (let r of this.roads.filter(r => r.source == b || r.destination == b)) {
            this.delete_road(r)
        }

        for (let iwp of b.occupied_tiles()) {
            this.mark_unoccupied(iwp)
        }

        this.buildings = this.buildings.filter(ab => ab !== b)
        this.distributor.update_from(this.buildings, this.roads)
        b.notify_did_change()
    }

    public find_route(start: WorldPoint, finish: WorldPoint): Route | null {
        // TODO it would be more efficient to clamp this to the occupied rect

        // existence implies visited, the direction is how we entered
        let vertices:Map<number, Direction> = new Map<number, Direction>()

        let pq = new PQueue<WorldPoint>()
        pq.push({ 
            distance: 0,
            data: start.copy(),
        })

        while (true) {
            const rec = pq.pop()
            if (rec == null) {
                return null
            }

            if (rec.data.equal_to(finish)) {
                let r:Route = {
                    source: start,
                    destination: finish,
                    directions: [],
                }

                let working = finish.copy()
                while (!working.equal_to(start)) {
                    const d: Direction | undefined = vertices.get(this.map.tile_key(working))
                    if (d == undefined) {
                        throw new Error("vertex missing")
                    }
                    r.directions.push(d)
                    working.translate(opposite(d))
                }
                r.directions.reverse()
                return r
            }

            for (let d of all_directions()) {
                const next_pos = rec.data.translated(d)

                if (!next_pos.equal_to(finish) && !this.valid_route_point(next_pos)) {
                    continue
                }

                const key = this.map.tile_key(next_pos)
                if (vertices.has(key)) {
                    continue
                }

                vertices.set(key, d)
                pq.push({
                    distance: rec.distance + 1,
                    data: next_pos,
                })
            }
        }
    }

    // find a road between two buildings
    public find_road(start: Building, finish: Building): Road | null {
        const rt = this.find_route(start.position, finish.position)
        if (rt == null) {
            return null
        }

        return new Road(start, finish, rt)
    }

    // find a road passing through the specified tile
    public find_road_at(pos: WorldPoint): Road | null {
        for (let rd of this.roads) {
            for (let rp of rd.positions()) {
                if (rp.equal_to(pos)) {
                    return rd
                }
            }
        }

        return null
    }
}

